import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

type ComparisonRow = {
  label: string
  automated: string
  manual: string
}

const WORKFLOW_DATA: ComparisonRow[] = [
  {
    label: "Steps to produce one video",
    automated: "3 steps (paste listing, choose style, export)",
    manual: "9 steps (brief, script, shoot, edit, review, revise, export, format, upload)",
  },
  {
    label: "Time per video",
    automated: "~60 seconds",
    manual: "30-60 minutes (DIY) or 3-5 business days (agency)",
  },
  {
    label: "Technical skill required",
    automated: "None; browser-based, no software to install",
    manual: "Intermediate to advanced (editing software, motion graphics)",
  },
  {
    label: "Turnaround for 10 listings",
    automated: "Under 15 minutes",
    manual: "5-10 hours (DIY) or 2-4 weeks (agency)",
  },
  {
    label: "Output formats",
    automated: "All platforms (9:16, 1:1, 16:9) in one click",
    manual: "Each format requires a separate export and re-edit",
  },
]

const QUALITY_DATA: ComparisonRow[] = [
  {
    label: "Brand consistency",
    automated: "Guaranteed; templates enforce colours, fonts, and logos",
    manual: "Varies by editor; inconsistencies creep in over time",
  },
  {
    label: "Music and pacing",
    automated: "Pre-tested transitions and licensed music included",
    manual: "Depends on editor taste; licensing often overlooked",
  },
  {
    label: "Text overlays and captions",
    automated: "Auto-generated from listing data (price, beds, location)",
    manual: "Typed manually; prone to errors and outdated information",
  },
  {
    label: "Updates when listing changes",
    automated: "Re-generate in seconds with new data",
    manual: "Re-edit from scratch or pay for revisions",
  },
  {
    label: "Quality floor",
    automated: "Every video meets a professional baseline",
    manual: "Wide range: some excellent, many mediocre",
  },
]

const COST_DATA: ComparisonRow[] = [
  {
    label: "Cost per video",
    automated: "From £0.98 per video",
    manual: "£50-200 per video (agency) or £0 + your time (DIY)",
  },
  {
    label: "Monthly cost for 20 listings",
    automated: "~£20 (platform subscription)",
    manual: "£1,000-4,000 (agency) or 10-20 hours of your time",
  },
  {
    label: "Hidden costs",
    automated: "None; pricing is transparent and predictable",
    manual: "Revisions, music licensing, additional formats, staff overtime",
  },
  {
    label: "Scales with portfolio",
    automated: "Yes; marginal cost per video stays flat",
    manual: "No; costs and time grow linearly with each listing",
  },
  {
    label: "Contract commitment",
    automated: "Monthly; cancel any time",
    manual: "Per-project or retainer (often 3-6 month minimum)",
  },
]

const MANUAL_STEPS = [
  { step: "Write the brief and gather assets", time: "10 min" },
  { step: "Script the voiceover or text overlays", time: "15 min" },
  { step: "Select and sequence photos or footage", time: "10 min" },
  { step: "Import into editing software and arrange", time: "10 min" },
  { step: "Add transitions, music, and branding", time: "15 min" },
  { step: "Export a first draft and review", time: "5 min" },
  { step: "Make revisions (at least one round)", time: "10 min" },
  { step: "Export for each platform format", time: "10 min" },
  { step: "Upload and schedule across channels", time: "5 min" },
]

const FAQ = [
  {
    q: "Is automated video good enough for property marketing?",
    a: "Yes. Modern automation platforms like QuiteRight produce broadcast-quality video using professional templates, licensed music, and cinematic transitions. The output is indistinguishable from manually edited content, and consistency is actually higher because every video follows the same brand standards.",
  },
  {
    q: "How much time does automated video save per listing?",
    a: "On average, you save 30 to 60 minutes per listing compared to manual editing. For an agency with 20 active listings, that translates to 10 to 20 hours saved every month, time that can be redirected to valuations, viewings, and client relationships.",
  },
  {
    q: "Can I customise automated videos with my own branding?",
    a: "Absolutely. QuiteRight lets you set your agency colours, logo, fonts, and contact details once. Every video you generate automatically includes your branding, so your content is always on-brand without any manual effort.",
  },
  {
    q: "What about luxury properties that need bespoke content?",
    a: "For ultra-premium listings (typically £2 million and above), a bespoke video shoot with drone footage and professional narration can add value. However, even in this segment, automated video works brilliantly for social media teasers, portal listings, and initial marketing while you arrange the bespoke production.",
  },
  {
    q: "Do I need any technical skills to use automated video tools?",
    a: "No. QuiteRight is designed for estate agents, not video editors. You paste a listing URL or upload photos, choose a style, and the platform does the rest. If you can use Rightmove, you can use QuiteRight.",
  },
  {
    q: "How do automated videos perform on social media compared to manual ones?",
    a: "Performance depends on content relevance and timing, not production method. Automated videos from QuiteRight use the same proven formats (vertical for Reels and TikTok, square for feeds) and follow platform best practices. Agencies using QuiteRight report engagement rates on par with or above manually produced content.",
  },
  {
    q: "Can I still edit an automated video if I want to tweak something?",
    a: "Yes. QuiteRight gives you control over text, photo order, music, and pacing before you export. You can also regenerate a video with different settings in seconds, something that would take another 30 minutes with manual editing software.",
  },
  {
    q: "What is the real cost difference for a mid-size agency?",
    a: "A typical agency with 15 to 25 active listings spends £750 to £5,000 per month on outsourced video, or allocates 15 to 25 hours of staff time to DIY editing. With QuiteRight, the same output costs under £25 per month and takes less than 30 minutes total. The savings are significant and immediate.",
  },
]

/* ------------------------------------------------------------------ */
/*  Helper components                                                  */
/* ------------------------------------------------------------------ */

function Badge({ variant, children }: { variant: "automated" | "manual"; children: React.ReactNode }) {
  const styles =
    variant === "automated"
      ? "bg-red/5 text-red border-red/20"
      : "bg-charcoal/5 text-charcoal border-charcoal/20"

  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${styles}`}>
      {children}
    </span>
  )
}

function ComparisonTable({
  title,
  subtitle,
  data,
}: {
  title: string
  subtitle: string
  data: ComparisonRow[]
}) {
  return (
    <section className="border-t border-stone px-6 py-16">
      <div className="mx-auto max-w-4xl">
        <h2 className="font-display text-2xl font-bold text-charcoal">
          {title}
          <span className="text-red">.</span>
        </h2>
        <p className="mt-2 text-sm text-slate">{subtitle}</p>

        <div className="mt-6 overflow-x-auto rounded-xl border border-stone">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone bg-cream/50">
                <th className="px-4 py-3 text-left font-medium text-charcoal">
                  Factor
                </th>
                <th className="px-4 py-3 text-left font-medium text-charcoal">
                  <Badge variant="automated">Automated</Badge>
                </th>
                <th className="px-4 py-3 text-left font-medium text-charcoal">
                  <Badge variant="manual">Manual</Badge>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone">
              {data.map((row) => (
                <tr key={row.label} className="hover:bg-cream/30">
                  <td className="px-4 py-3 font-medium text-charcoal">
                    {row.label}
                  </td>
                  <td className="px-4 py-3 text-slate">{row.automated}</td>
                  <td className="px-4 py-3 text-slate">{row.manual}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className={className}
    >
      <path
        fillRule="evenodd"
        d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function ArrowIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className="size-3.5 transition-transform group-hover:translate-x-0.5"
    >
      <path
        fillRule="evenodd"
        d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function ChevronIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
    >
      <path
        fillRule="evenodd"
        d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function AutomatedVsManualCampaigns({
  meta,
}: {
  meta: SeoMeta
}) {
  const totalManualMinutes = MANUAL_STEPS.reduce(
    (sum, s) => sum + parseInt(s.time),
    0,
  )

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline:
              "Automated vs Manual Video Ad Campaigns for Property",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-03-12",
            dateModified: "2026-03-12",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema
          canonical={meta.canonical}
          items={[
            { name: "Guides", path: "/guides" },
            { name: "Automated vs Manual Video Ad Campaigns for Property" },
          ]}
        />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Automated vs manual video ad campaigns for property
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Creating property videos manually is slow, expensive, and
            impossible to scale. Automation changes everything. Here is
            a full comparison to help you choose the right approach for
            your agency. See <Link href="/how-it-works" className="text-red underline decoration-red/30 hover:text-charcoal">how QuiteRight works</Link> in
            practice.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Badge variant="automated">Automated</Badge>
            <span className="text-sm text-slate">vs</span>
            <Badge variant="manual">Manual</Badge>
          </div>
        </div>
      </section>

      <SocialShareBar />

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            { stat: "60s", label: "per video with automation" },
            { stat: "100%", label: "brand consistency, every time" },
            { stat: "∞", label: "videos per month, no bottleneck" },
            { stat: "£0.98", label: "per video from (vs £50-200 manual)" },
          ].map((item) => (
            <div
              key={item.stat}
              className="bg-charcoal px-5 py-6 text-center"
            >
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">
                {item.label}
              </p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Deep-dive: Automated vs Manual */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Two approaches, very different outcomes
            <span className="text-red">.</span>
          </h2>
          <p className="mt-2 text-sm text-slate">
            Both automated and manual methods can produce property
            video. Tools like{" "}
            <a href="https://www.canva.com" target="_blank" rel="noopener noreferrer" className="text-red underline decoration-red/30 hover:text-charcoal">Canva</a> and{" "}
            <a href="https://www.adobe.com/products/premiere.html" target="_blank" rel="noopener noreferrer" className="text-red underline decoration-red/30 hover:text-charcoal">Adobe Premiere</a>{" "}
            give you manual control, while platforms like QuiteRight automate
            the entire workflow. The difference is in what it costs you in time,
            money, and consistency at scale. See how we compare in our{" "}
            <Link href="/quiteright-vs-canva" className="text-red underline decoration-red/30 hover:text-charcoal">QuiteRight vs Canva</Link>{" "}
            breakdown.
          </p>

          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            {/* Automated card */}
            <div className="rounded-xl border-2 border-red/20 bg-white p-6">
              <Badge variant="automated">Automated</Badge>
              <h3 className="mt-4 font-display text-lg font-bold text-charcoal">
                Tools like QuiteRight
              </h3>
              <ul className="mt-4 space-y-2.5 text-sm text-slate">
                {[
                  "Paste a listing URL and get a video in 60 seconds",
                  "Brand colours, logo, and fonts applied automatically",
                  "All social formats (Reels, TikTok, feed) generated at once",
                  "Consistent quality across every listing, every time",
                  "No editing software, no learning curve, no freelancers",
                  "Re-generate instantly when price or details change",
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <CheckIcon className="mt-0.5 size-4 shrink-0 text-red" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Manual card */}
            <div className="rounded-xl border border-stone bg-white p-6">
              <Badge variant="manual">Manual</Badge>
              <h3 className="mt-4 font-display text-lg font-bold text-charcoal">
                DIY or agency production
              </h3>
              <ul className="mt-4 space-y-2.5 text-sm text-slate">
                {[
                  "Write a brief, script overlays, sequence photos manually",
                  "Edit in Premiere Pro, Final Cut, CapCut, or similar",
                  "Export separately for each platform and aspect ratio",
                  "Quality depends entirely on the editor's skill and time",
                  "Outsourcing costs £50-200 per video with multi-day turnaround",
                  "Every price change means re-editing from scratch",
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <CheckIcon className="mt-0.5 size-4 shrink-0 text-slate" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison tables */}
      <ComparisonTable
        title="Workflow and speed"
        subtitle="How quickly you go from listing to published video."
        data={WORKFLOW_DATA}
      />

      <ComparisonTable
        title="Quality and consistency"
        subtitle="What the finished product looks like, and whether it stays on-brand."
        data={QUALITY_DATA}
      />

      <ComparisonTable
        title="Cost and scalability"
        subtitle="What it actually costs to produce video at volume."
        data={COST_DATA}
      />

      {/* The real cost of manual */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            The real cost of manual video production
            <span className="text-red">.</span>
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate">
            Even if you handle everything in-house, the time adds up
            fast. Here is a realistic breakdown for creating a single
            property video manually. For context on how this cost compares
            to other channels, see our{" "}
            <Link href="/guides/cost-per-lead-video-ads-vs-traditional-property-marketing" className="text-red underline decoration-red/30 hover:text-charcoal">cost per lead guide</Link>.
          </p>

          <div className="mt-6 overflow-x-auto rounded-xl border border-stone">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone bg-cream/50">
                  <th className="px-4 py-3 text-left font-medium text-charcoal">
                    Step
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-charcoal">
                    Task
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-charcoal">
                    Time
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone">
                {MANUAL_STEPS.map((row, i) => (
                  <tr key={row.step} className="hover:bg-cream/30">
                    <td className="px-4 py-3 font-medium text-charcoal">
                      {i + 1}
                    </td>
                    <td className="px-4 py-3 text-slate">{row.step}</td>
                    <td className="px-4 py-3 text-right text-slate">
                      {row.time}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-charcoal/10 bg-cream/30">
                  <td colSpan={2} className="px-4 py-3 font-medium text-charcoal">
                    Total per video
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-red">
                    {totalManualMinutes} min
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p className="mt-4 text-sm leading-relaxed text-slate">
            Multiply that by 15 to 20 listings per month and you are
            looking at{" "}
            <strong className="text-charcoal">
              {Math.round((totalManualMinutes * 17.5) / 60)} hours
            </strong>{" "}
            of video production every month. That is nearly two full
            working days spent on a task that automation handles in
            under 20 minutes.
          </p>
        </div>
      </section>

      {/* When manual still makes sense */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            When manual production still makes sense
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Automation is the right choice for the vast majority of{" "}
              <Link href="/guides/property-video-marketing" className="text-red underline decoration-red/30 hover:text-charcoal">property video marketing</Link>.
              But there are situations where
              bespoke, manually produced video adds genuine value.
            </p>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-stone bg-white p-5">
              <h3 className="font-display text-base font-bold text-charcoal">
                Luxury and prestige properties
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate">
                Properties above £2 million often benefit from drone
                footage, professional narration, and cinematic
                production. Buyers at this level expect a premium
                experience, and the marketing budget justifies it.
              </p>
            </div>

            <div className="rounded-xl border border-stone bg-white p-5">
              <h3 className="font-display text-base font-bold text-charcoal">
                Bespoke brand campaigns
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate">
                Agency-level brand films, recruitment videos, or
                campaign content that tells a broader story. These
                are not listing-specific and need a creative director,
                not a template.
              </p>
            </div>
          </div>

          <p className="mt-6 text-sm leading-relaxed text-slate">
            Even in these cases, most agencies use automated video for
            their day-to-day listings and reserve manual production for
            the select few that warrant the investment. The two
            approaches complement each other. For more on the DIY trade-offs,
            see{" "}
            <Link href="/quiteright-vs-diy-video" className="text-red underline decoration-red/30 hover:text-charcoal">QuiteRight vs DIY video</Link>.
          </p>
        </div>
      </section>

      {/* QuiteRight callout */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <div className="rounded-2xl border-2 border-red/30 bg-white p-8 sm:p-10">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                Featured
              </p>
              <h2 className="mt-3 font-display text-2xl font-bold text-charcoal sm:text-3xl">
                QuiteRight: the automation that works
                <span className="text-red">.</span>
              </h2>
              <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
                <p>
                  QuiteRight was built specifically for UK estate agents
                  who want professional video marketing without the
                  overhead. Paste a{" "}
                  <a href="https://www.rightmove.co.uk" target="_blank" rel="noopener noreferrer" className="text-red underline decoration-red/30 hover:text-charcoal">Rightmove</a>,{" "}
                  <a href="https://www.zoopla.co.uk" target="_blank" rel="noopener noreferrer" className="text-red underline decoration-red/30 hover:text-charcoal">Zoopla</a>, or OnTheMarket
                  listing, choose a style, and get a scroll-stopping
                  video ad in 60 seconds. See our{" "}
                  <Link href="/integrations/rightmove" className="text-red underline decoration-red/30 hover:text-charcoal">Rightmove integration</Link>{" "}
                  and{" "}
                  <Link href="/integrations/zoopla" className="text-red underline decoration-red/30 hover:text-charcoal">Zoopla integration</Link>{" "}
                  pages for details.
                </p>
                <p>
                  Every video includes your agency branding, licensed
                  music, cinematic transitions, and optimised formatting
                  for Instagram Reels, TikTok, Facebook, and more. No
                  editing software. No freelancers. No waiting. Explore
                  our full{" "}
                  <Link href="/features" className="text-red underline decoration-red/30 hover:text-charcoal">feature set</Link>{" "}
                  to see what is included.
                </p>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                {[
                  { stat: "3 steps", desc: "Paste, style, export" },
                  { stat: "60 seconds", desc: "From listing to video" },
                  { stat: "From £0.98", desc: "Per video, all formats included" },
                ].map((item) => (
                  <div
                    key={item.stat}
                    className="rounded-lg bg-red/5 px-4 py-3 text-center"
                  >
                    <p className="font-display text-lg font-bold text-red">
                      {item.stat}
                    </p>
                    <p className="mt-1 text-xs text-slate">{item.desc}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  href="/sign-up"
                  className="inline-flex items-center justify-center rounded-lg bg-red px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-deep-red"
                >
                  Start your free trial
                </Link>
                <Link
                  href="/features"
                  className="inline-flex items-center justify-center rounded-lg border border-stone px-6 py-3 text-sm font-medium text-charcoal transition-colors hover:border-red/30 hover:text-red"
                >
                  See how it works
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <ChevronIcon />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related guides */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related guides<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              {
                href: "/guides/property-video-marketing",
                title: "Property video marketing",
                desc: "The complete guide to using video to sell more properties.",
              },
              {
                href: "/guides/instagram-reels-estate-agents",
                title: "Instagram Reels for estate agents",
                desc: "How to create Reels that generate enquiries and win instructions.",
              },
              {
                href: "/guides/social-media-estate-agents",
                title: "Social media for estate agents",
                desc: "Build a social presence that attracts vendors and buyers.",
              },
              {
                href: "/guides/instagram-vs-facebook-vs-tiktok-property-ads",
                title: "Instagram vs Facebook vs TikTok for property ads",
                desc: "Which platform delivers the best return for property video.",
              },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">
                  {item.desc}
                </p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Explore
                  <ArrowIcon />
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Stop editing. Start scaling
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Every minute you spend editing video is a minute you are
            not spending on valuations, viewings, and winning
            instructions. QuiteRight gives you professional property
            video in 60 seconds, so you can focus on what actually
            grows your agency.{" "}
            <Link href="/sign-up" className="text-red underline decoration-red/30 hover:text-cream">Get started free</Link>{" "}
            and see the difference.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
